<script setup lang="ts">
import type { Diagnostic } from "@/types";
import DiagnosticItem from "./DiagnosticItem.vue";
import { computed, ref, toRef, watch } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { QueryKey } from "@/stores/queries/query-keys";
import {
  useCreateDiagnostic,
  useDeleteManyDiagnostics,
  useUpdateDiagnostic,
} from "@/stores/queries";
import ImportReportsDialog from "./ImportReportsDialog.vue";
import DRButtonDropdown from "../UI/DRButtonDropdown.vue";
import DRDisplayList from "../UI/items/DRDisplayList.vue";
import ConfirmationModal from "../generic/ConfirmationModal.vue";
import InputModal from "../generic/InputModal.vue";
import { useSelect } from "@/composable/useSelect";
import ProjectPapper from "../projects/ProjectPapper.vue";
import { useNavigate } from "@/composable/useNavigate";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const props = defineProps<{
  diagnostics: Diagnostic[];
  projectId: number;
}>();

const { navigateDiagnostic } = useNavigate();

const openDiagnostic = (diagnostic: Diagnostic) => {
  navigateDiagnostic(diagnostic.id);
};

const projectId = toRef(props, "projectId");
const queryClient = useQueryClient();

const { mutate: createDiagnostic } = useCreateDiagnostic(projectId);
const { mutateAsync: deleteMany, isPending: isDeleting } =
  useDeleteManyDiagnostics();

const handleCreate = () => {
  createDiagnostic(undefined, {
    onSuccess: (diagnostic) => {
      openDiagnostic(diagnostic);
    },
  });
};

const handleDropdownCommand = (command: string) => {
  if (command === "import") {
    showUploader.value = true;
  } else if (command === "create") {
    handleCreate();
  }
};

watch(
  () => props.diagnostics.map((d) => d.isExtractingData),
  (newValue) => {
    if (newValue.find((v) => v))
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTICS] });
      }, 2000);
  },
  {
    immediate: true,
    deep: true,
  }
);

const showUploader = ref(false);

const diagnosticsIds = computed(() =>
  props.diagnostics.map((diagnostic) => diagnostic.id)
);

const deleteSelection = async () => {
  deleteMany(selectedIds.value, {
    onSuccess: () => {
      setSelectedIds([]);
      showConfirmDelete.value = false;
      selectMode.value = false;
    },
  });
};

const showConfirmDelete = ref(false);

const { selectedIds, selectMode, setSelectedIds, toggleSelect } =
  useSelect(diagnosticsIds);

const selectDiagnostic = (diagnostic: Diagnostic) => {
  if (selectMode.value) {
    toggleSelect(diagnostic.id);
  } else {
    openDiagnostic(diagnostic);
  }
};

// Rename
const renamingDiagnostic = ref<Diagnostic | null>(null);
const renamingDiagnosticId = computed(() => renamingDiagnostic.value?.id);
const { mutateAsync: updateDiagnostic, isPending: isRenaming } =
  useUpdateDiagnostic(computed(() => renamingDiagnosticId.value));

const handleRename = (diagnostic: Diagnostic) => {
  renamingDiagnostic.value = diagnostic;
};

const handleSaveRename = async (name: string) => {
  if (renamingDiagnostic.value && name !== renamingDiagnostic.value.name) {
    await updateDiagnostic({ name });
  }
  renamingDiagnostic.value = null;
};
</script>

<template>
  <ProjectPapper class="my-3">
    <template #top-left>
      <span class="h5">{{ t("dashboard:project.sections.inspections") }}</span>

      <!-- <div class="d-flex align-items-center">
        <DRSelector
          :selectedIds="selectedIds"
          :itemsIds="diagnosticsIds"
          v-model:selectMode="selectMode"
          @selectAll="toggleAll"
        >
          <template #actions>
            <div class="d-flex align-items-center gap-2">
              <DRResponsiveBtn
                variant="flat"
                icon="bi-trash"
                @click="showConfirmDelete = true"
                :label="$t('dashboard:diagnostic.deleteMany.btnLabel')"
              />
            </div>
          </template>
        </DRSelector></div
    > -->
    </template>
    <template #top-right>
      <DRButtonDropdown
        icon="bi-plus-lg"
        variant="primary"
        :label="t('dashboard:diagnostic.add.button')"
        :size="35"
        :options="[
          {
            command: 'import',
            icon: 'bi-upload',
            title: t('dashboard:diagnostic.add.import.title'),
            description: t('dashboard:diagnostic.add.import.description'),
          },
          {
            command: 'create',
            icon: 'bi-plus-lg',
            title: t('dashboard:diagnostic.add.create.title'),
            description: t('dashboard:diagnostic.add.create.description'),
          },
        ]"
        @command="handleDropdownCommand"
      />
    </template>

    <div class="d-flex flex-column gap-2">
      <DRDisplayList :data="diagnostics" key-index="id" class="mt-2">
        <template #item="{ item, display }">
          <DiagnosticItem
            :diagnostic="item"
            @click="selectDiagnostic(item)"
            :display="display"
            :selected="selectedIds.includes(item.id)"
            @toggle-select="toggleSelect(item.id)"
            @rename="handleRename"
          />
        </template>
      </DRDisplayList>
    </div>
  </ProjectPapper>

  <ImportReportsDialog v-model="showUploader" :projectId="projectId" />
  <ConfirmationModal
    v-model="showConfirmDelete"
    :title="$t('dashboard:diagnostic.deleteMany.dialog.title')"
    :message="$t('dashboard:diagnostic.deleteMany.dialog.message')"
    :loading="isDeleting"
    :confirm-label="$t('delete')"
    danger
    @confirm="deleteSelection"
  />

  <InputModal
    :model-value="!!renamingDiagnostic"
    @update:model-value="!$event && (renamingDiagnostic = null)"
    :title="t('report:diagnostics.editName.title')"
    :value="renamingDiagnostic?.name"
    :placeholder="t('report:diagnostics.editName.placeholder')"
    :loading="isRenaming"
    @confirm="handleSaveRename"
  />
</template>

<style lang="scss" scoped></style>
